/**
 * Workflow Service
 *
 * API endpoints for investment workflow and status operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type {
  StatusHistory,
  InvestmentDecision,
  InvestmentComment,
  InvestmentStatus,
  CreateInvestmentDecisionDto,
  CreateInvestmentDecisionResponseDto,
} from '@medsuite/models';

export interface SubmitForApprovalDto {
  comments?: string;
}

export interface CreateCommentDto {
  investment_id: number;
  comment_text: string;
}

export interface UpdateCommentDto {
  comment_text: string;
}

export interface StatusChangeDto {
  new_status: InvestmentStatus;
  comments?: string;
}

/**
 * Decisions response from API
 */
export interface DecisionsResponse {
  investment_id: number;
  decisions: InvestmentDecision[];
  total: number;
  summary: {
    total_decisions: number;
    approved_count: number;
    deferred_count: number;
    rejected_count: number;
    cancelled_count: number;
    latest_decision: InvestmentDecision | null;
  };
}

/**
 * Workflow Service
 */
class WorkflowService {
  /**
   * Submit investment for approval
   */
  async submitForApproval(
    investmentId: number,
    data?: SubmitForApprovalDto
  ): Promise<ApiResponse<StatusHistory>> {
    return apiClient.post<StatusHistory>(`/investments/${investmentId}/submit`, data);
  }

  /**
   * Create investment decision (requires DecisionMaker role)
   *
   * Creates a decision record and updates the investment status.
   */
  async createDecision(
    investmentId: number,
    data: CreateInvestmentDecisionDto
  ): Promise<ApiResponse<CreateInvestmentDecisionResponseDto>> {
    return apiClient.post<CreateInvestmentDecisionResponseDto>(
      `/investments/${investmentId}/decisions`,
      data
    );
  }

  /**
   * Get all decisions for an investment
   */
  async getDecisions(investmentId: number): Promise<ApiResponse<DecisionsResponse>> {
    return apiClient.get<DecisionsResponse>(`/investments/${investmentId}/decisions`);
  }

  /**
   * Get latest decision for an investment
   */
  async getLatestDecision(
    investmentId: number
  ): Promise<ApiResponse<{ investment_id: number; decision: InvestmentDecision | null }>> {
    return apiClient.get<{ investment_id: number; decision: InvestmentDecision | null }>(
      `/investments/${investmentId}/decisions/latest`
    );
  }

  /**
   * Change investment status
   */
  async changeStatus(
    investmentId: number,
    data: StatusChangeDto
  ): Promise<ApiResponse<StatusHistory>> {
    return apiClient.patch<StatusHistory>(`/investments/${investmentId}/status`, data);
  }

  /**
   * Get status history for an investment
   */
  async getStatusHistory(investmentId: number): Promise<ApiResponse<StatusHistory[]>> {
    return apiClient.get<StatusHistory[]>(`/investments/${investmentId}/status-history`);
  }

  /**
   * Get comments for an investment
   */
  async getComments(investmentId: number): Promise<ApiResponse<InvestmentComment[]>> {
    return apiClient.get<InvestmentComment[]>(`/investments/${investmentId}/comments`);
  }

  /**
   * Add comment to investment
   */
  async addComment(
    investmentId: number,
    data: Omit<CreateCommentDto, 'investment_id'>
  ): Promise<ApiResponse<InvestmentComment>> {
    return apiClient.post<InvestmentComment>(`/investments/${investmentId}/comments`, {
      ...data,
      investment_id: investmentId,
    });
  }

  /**
   * Update comment
   */
  async updateComment(
    investmentId: number,
    commentId: number,
    data: UpdateCommentDto
  ): Promise<ApiResponse<InvestmentComment>> {
    return apiClient.put<InvestmentComment>(
      `/investments/${investmentId}/comments/${commentId}`,
      data
    );
  }

  /**
   * Delete comment
   */
  async deleteComment(investmentId: number, commentId: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/investments/${investmentId}/comments/${commentId}`);
  }
}

export const workflowService = new WorkflowService();
